import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  DAY_LABELS,
  toWeekDateKey,
  type DraftTask,
} from "../../utils/timesheetWeek";
import { Input, TextArea } from "../common";
import Modal2, { Modal2Footer, Modal2Button } from "../common/Modal2";

interface TaskModalProps {
  open: boolean;
  day: Date;
  /** When null/undefined, the modal is in "add" mode. Otherwise it edits this task. */
  initial?: DraftTask | null;
  isSubmitting?: boolean;
  onClose: () => void;
  onSave: (task: DraftTask) => void;
}

interface FormState {
  name: string;
  project: string;
  startTime: string;
  endTime: string;
  description: string;
}

const EMPTY_FORM: FormState = {
  name: "",
  project: "",
  startTime: "",
  endTime: "",
  description: "",
};

const TaskModal: React.FC<TaskModalProps> = ({
  open,
  day,
  initial = null,
  isSubmitting = false,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const isEdit = !!initial;

  useEffect(() => {
    if (!open) return;
    setForm(
      initial
        ? {
            name: initial.name,
            project: initial.project,
            startTime: initial.startTime,
            endTime: initial.endTime,
            description: initial.description,
          }
        : EMPTY_FORM,
    );
  }, [open, initial]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error("Please enter a task name");
      return;
    }
    if (!form.project.trim()) {
      toast.error("Please enter a project");
      return;
    }
    if (!form.startTime || !form.endTime) {
      toast.error("Please select start and end time");
      return;
    }
    if (form.endTime <= form.startTime) {
      toast.error("End time must be greater than start time");
      return;
    }

    const task: DraftTask = {
      key:
        initial?.key ??
        `${toWeekDateKey(day)}-${initial == null ? Date.now() : ""}`,
      name: form.name.trim(),
      project: form.project.trim(),
      startTime: form.startTime,
      endTime: form.endTime,
      description: form.description.trim(),
    };
    onSave(task);
    onClose();
  };

  const dayLabel = `${DAY_LABELS[day.getDay()]} ${String(
    day.getDate(),
  ).padStart(2, "0")} ${toWeekDateKey(day).slice(0, 4)}`;

  return (
    <Modal2
      isOpen={open}
      onClose={isSubmitting ? () => {} : onClose}
      title={isEdit ? `Edit task · ${dayLabel}` : `Add task · ${dayLabel}`}
      size="sm"
      closable={!isSubmitting}
      maskClosable={false}
      footer={
        <Modal2Footer justify="end">
          <Modal2Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Modal2Button>
          <Modal2Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isEdit ? "Save Changes" : "Add Task"}
          </Modal2Button>
        </Modal2Footer>
      }
    >
      <div className="space-y-4">
        <Input
          label="Task Name"
          required
          value={form.name}
          placeholder="Enter task name"
          onChange={(v) => setForm((f) => ({ ...f, name: String(v) }))}
          disabled={isSubmitting}
        />
        <Input
          label="Project"
          required
          value={form.project}
          placeholder="Enter project name"
          onChange={(v) => setForm((f) => ({ ...f, project: String(v) }))}
          disabled={isSubmitting}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input
            type="time"
            label="Start Time"
            required
            value={form.startTime}
            disabled={isSubmitting}
            onChange={(v) => setForm((f) => ({ ...f, startTime: String(v) }))}
          />
          <Input
            type="time"
            label="End Time"
            required
            value={form.endTime}
            disabled={isSubmitting}
            onChange={(v) => setForm((f) => ({ ...f, endTime: String(v) }))}
          />
        </div>

        <TextArea
          label="Description"
          value={form.description}
          onChange={(val) => setForm((f) => ({ ...f, description: String(val) }))}
          placeholder="Describe the work performed (optional)"
          minRows={3}
          maxLength={500}
        />
      </div>
    </Modal2>
  );
};

export default TaskModal;